import React, { useState, useEffect, useCallback } from "react";
import {
  LoadingIndicator,
  ErrorMessage,
  Dashboard,
  MyCourses,
  MyBatches,
  MyTutors,
  MyAssignments,
  MyResults,
  MyConcepts,
  Sidebar,
  Header,
} from "./StudentHub";
import useStudentEnrollmentData from "shared/hooks/useStudentEnrollmentData";
import { useMasterList } from "shared/hooks/useMasterList";
import masterApiCall from "shared/services/MasterDataApi";
import { useLoading } from "shared/contexts/Loading";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";

// Adaptive Learning Imports
import AdaptiveLearningDashboard from "modules/adaptivelearning/AdaptiveLearningDashboard";
import LearningSession from "modules/adaptivelearning/LearningSession";
import ResultsPage from "modules/adaptivelearning/ResultsPage";

export default function StudentsApp({ navigateTo, studentId, displayToast }) {
  const [activeSection, setActiveSection] = useState("dashboard");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [adaptiveLearningData, setAdaptiveLearningData] = useState({
    conceptCode: null,
    sessionId: null,
    sessionData: null,
    previousTier: null,
    conceptProgress: null,
  });
  const { startLoading, stopLoading } = useLoading();


  const {
    masterData,
    isLoading: masterDataLoading,
    error: masterDataError
  } = useMasterList({ useMockData, displayToast, startLoading, stopLoading, apiCall: masterApiCall });


  // Dark mode logic removed since Tailwind handles it automatically via the 'dark' class
  // useEffect(() => {
  //   document.documentElement.classList.toggle("dark", isDarkMode);
  // }, [isDarkMode]);

  const joinQuiz = () => navigateTo("joinQuiz", studentId);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  // Fetch enrollments + related entities
  const {
    students,
    enrollments,
    courses,
    batches,
    tutors,
    loading: enrollmentLoading,
    error: enrollmentError,
  } = useStudentEnrollmentData({ studentId: studentId || null });

  // Combine loading and error states
  const loading = enrollmentLoading;
  const error = enrollmentError;

  const renderSection = () => {
    if (loading) return <LoadingIndicator />;
    if (error) return <ErrorMessage message={error} />;

    const student = students && students.length > 0 ? students[0] : null;
    switch (activeSection) {
      case "dashboard":
        return (
          <Dashboard
            setActiveSection={setActiveSection}
            student={student}
            courses={courses}
            assignments={[]} // You can add assignment logic later
            batches={batches}
          />
        );
      case "my-courses":
        return <MyCourses student={student} courses={courses} masterData={masterData} />;
      case "my-batches":
        return <MyBatches student={student} batches={batches} />;
      case "my-concepts":
        return (
          // <ProtectedVisibility requiredRoles={['ROLE_STUDENT']} beta={false}>
            <MyConcepts
              student={student}
              courses={courses}
              batches={batches}
              masterData={masterData}
              displayToast={displayToast}
              onStartLearning={(conceptProgress) => {
                setAdaptiveLearningData({
                  conceptCode: conceptProgress.conceptCode,
                  conceptProgress: conceptProgress,
                  sessionId: null,
                });
                setActiveSection("adaptive-learning-session");
              }}
            />
          // </ProtectedVisibility>
        );
      case "my-tutors":
        return <MyTutors tutors={tutors} />;
      case "my-assignments":
        return (
          <MyAssignments
            student={student}
            assignments={[]}
            displayToast={displayToast}
          />
        );
      case "my-results":
        return <MyResults student={student} displayToast={displayToast} />;
      case "adaptive-learning-session":
        return (
          <LearningSession
            studentId={student?.id} // Pass the integer database ID
            conceptCode={adaptiveLearningData.conceptCode}
            conceptProgress={adaptiveLearningData.conceptProgress}
            onComplete={(sessionId, sessionData, previousTier) => {
              setAdaptiveLearningData({
                ...adaptiveLearningData,
                sessionId: sessionId,
                sessionData: sessionData,
                previousTier: previousTier,
              });
              setActiveSection("adaptive-learning-results");
            }}
            onBack={() => setActiveSection("my-concepts")}
          />
        );
      case "adaptive-learning-results":
        return (
          <ResultsPage
            sessionId={adaptiveLearningData.sessionId}
            sessionData={adaptiveLearningData.sessionData}
            previousTier={adaptiveLearningData.previousTier}
            onContinueLearning={() => {
              setAdaptiveLearningData({
                ...adaptiveLearningData,
                sessionId: null,
                sessionData: null,
              });
              setActiveSection("adaptive-learning-session");
            }}
            onGoToDashboard={() => setActiveSection("my-concepts")}
          />
        );
      default:
        return (
          <Dashboard
            setActiveSection={setActiveSection}
            student={student}
            courses={courses}
            assignments={[]}
            batches={batches}
          />
        );
    }
  };
  const student = students && students.length > 0 ? students[0] : null;
  console.log(student);
  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      <Sidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 main-content custom-scroll pt-20 md:pt-4">
        <div className="max-w-7xl mx-auto">
          {/* Mobile Menu Button - in body content */}
          <button
            id="mobile-menu-button"
            className="md:hidden mb-4 p-3 rounded-lg bg-gray-700 text-white text-xl flex items-center justify-center hover:bg-gray-600 active:bg-gray-800 transition-colors shadow-md"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSidebar();
            }}
            aria-label="Toggle sidebar"
            aria-expanded={isSidebarOpen || false}
            type="button"
          >
            <span className="mr-2">☰</span>
            <span className="text-sm font-medium">Menu</span>
          </button>

          {/* Header Section */}
          <div className="mb-8">
            <Header
              student={student}
              useMockData={useMockData}
              setUseMockData={setUseMockData}
              joinQuiz={joinQuiz}
            />
          </div>

          {/* Content Section */}
          <div className="mt-8">{renderSection()}</div>
        </div>
      </main>
    </div>
  );
}